/**
 * Pure mappers that turn live platform data into right-rail workspace items.
 *
 * Notes come from the shared domain feed (insights + alerts). Feed to-do items
 * are built in `feedTodos.ts`. Keeping the mapping pure (data in, view model
 * out) makes it trivially testable and keeps all fetching in the hook layer.
 */
import {
  bookingHref,
  customerBookingsHref,
  feedEventHref,
  laneBookingsHref,
  operatorBookingsHref,
} from "@/features/feed/feedLinks";
import { feedPrivacyScope } from "@/features/feed/feedPrivacy";
import type { FeedEvent, FeedEventType } from "@/types/feed";
import type { WorkspaceNote } from "@/types/workspace";

const NOTES_LIMIT = 3;

/** Calculated / retrospective events surface as "Insight" notes. */
const INSIGHT_EVENT_TYPES = new Set<FeedEventType>([
  "insight_snapshot",
  "pattern_detected",
]);

/** Disruption / action events surface as "Alert" notes. */
const ALERT_EVENT_TYPES = new Set<FeedEventType>([
  "alert_fired",
  "booking_rolled",
  "vessel_delayed",
  "cutoff_approaching",
]);

/** Deep link for a feed event, falling back to its entity scope when it has no reference. */
function noteHref(event: FeedEvent): string | null {
  const direct = feedEventHref(event);
  if (direct) return direct;

  const scope = event.entity_scope ?? {};
  if (scope.booking_ids && scope.booking_ids.length > 0) {
    return bookingHref(scope.booking_ids[0]);
  }
  if (scope.lane_key) return laneBookingsHref(scope.lane_key);
  if (scope.consignee) return customerBookingsHref(scope.consignee);
  if (scope.opened_by) return operatorBookingsHref(scope.opened_by);
  return null;
}

/**
 * Select up to three feed events (newest first) as workspace notes. Insight and
 * pattern snapshots become "Insight"; disruptions and urgent events become
 * "Alert". Everything else is ignored.
 */
export function mapFeedEventsToNotes(
  events: FeedEvent[],
  limit = NOTES_LIMIT,
): WorkspaceNote[] {
  const notes: WorkspaceNote[] = [];

  for (const event of events) {
    let tag: WorkspaceNote["tag"] | null = null;
    if (INSIGHT_EVENT_TYPES.has(event.event_type)) {
      tag = "Insight";
    } else if (
      ALERT_EVENT_TYPES.has(event.event_type) ||
      event.severity === "urgent"
    ) {
      tag = "Alert";
    }
    if (!tag) continue;

    notes.push({
      id: event.id,
      title: event.title,
      body: event.summary ?? "",
      tag,
      href: noteHref(event),
      privacyScope: feedPrivacyScope(event),
    });
    if (notes.length >= limit) break;
  }

  return notes;
}
